// Clean VariablesSidebar.js - Remove debug spam, keep function debugging

import React, { useState } from 'react';
import { SYSTEM_VARIABLES } from './questionUtils';
import { getFunctionsForType } from '../functionRegistry';
import "../schema_builder.css";

export default function VariablesSidebar({ questions = [], onInsertVariable }) {

  const handleVariableClick = (variableName) => {
    onInsertVariable?.(variableName);
  };

  const handleFunctionClick = (functionName, variableName) => {
    const cleanName = variableName.replace(/^\{\{|\}\}$/g, '');
    const functionCall = `[[ ${functionName}({{${cleanName}}}) ]]`;

    // ✅ KEEP: Only function click debugging
    console.log('🔧 SIDEBAR: Function clicked ->', {
      functionName,
      variableName,
      functionCall
    });

    onInsertVariable?.(functionCall);
  };

  return (
    <div className="card h-100">
      <div className="card-header">
        <h6 className="mb-0">Available Variables</h6>
        <small className="text-muted">Click to insert variable or function</small>
      </div>
      <div className="card-body p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>

        {/* User Questions with Functions */}
        <div className="bg-primary bg-opacity-10 px-3 py-2 border-bottom">
          <small className="fw-medium text-primary">
            📝 YOUR QUESTIONS ({questions?.length || 0})
          </small>
        </div>

        {questions && questions.length > 0 ? (
          questions.map((question, index) => (
            <React.Fragment key={`${question.id || index}-${question.variable_name}`}>
              <VariableWithFunctions
                variable={{
                  name: question.variable_name,
                  type: question.variable_type || 'custom'
                }}
                description={question.label}
                onInsertVariable={handleVariableClick}
                onInsertFunction={handleFunctionClick}
              />

              {question.data === 'get_script_scenes_for_drop_down' && (
                <VariableWithFunctions
                  variable={{
                    name: `${question.variable_name}_text`,
                    type: 'scene_text'
                  }}
                  description={`Auto-loaded text for: ${question.label}`}
                  onInsertVariable={handleVariableClick}
                  onInsertFunction={handleFunctionClick}
                />
              )}
            </React.Fragment>
          ))
        ) : (
          <div className="px-3 py-3 text-muted text-center">
            <small>No questions added yet. Add questions to create variables.</small>
          </div>
        )}

        {/* System Variables */}
        <div className="bg-warning bg-opacity-10 px-3 py-2 border-bottom">
          <small className="fw-medium text-warning-emphasis">
            ⚙️ ALWAYS AVAILABLE ({SYSTEM_VARIABLES.length})
          </small>
        </div>

        {SYSTEM_VARIABLES.map((variable) => (
          <div
            key={variable.name}
            className="px-3 py-2 border-bottom cursor-pointer hover-bg-light"
            onClick={() => handleVariableClick(variable.name)}
            title={`Insert {{${variable.name}}} - ${variable.description}`}
          >
            <div className="d-flex align-items-center">
              <code className="text-warning-emphasis me-2">
                {`{{${variable.name}}}`}
              </code>
              <small className="text-muted text-truncate flex-grow-1">
                {variable.description}
              </small>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VariableWithFunctions({ variable, description, onInsertVariable, onInsertFunction }) {
  const [showFunctions, setShowFunctions] = useState(false);
  const availableFunctions = getFunctionsForType(variable.type);

  const handleFunctionClick = (functionName, variableName) => {
    onInsertFunction(functionName, variableName);
    setShowFunctions(false);
  };

  if (!variable?.name) {
    return null;
  }

  return (
    <div className="border rounded m-2 bg-light">
      <div className="p-2">
        <div className="d-flex align-items-center">
          <div
            className="flex-grow-1 cursor-pointer hover-bg-white rounded px-2 py-1"
            onClick={() => onInsertVariable(variable.name)}
            title={`Insert {{${variable.name}}} - ${description}`}
          >
            <div className="d-flex align-items-center">
              <code className="text-primary me-2">
                {`{{${variable.name}}}`}
              </code>
              <small className="text-muted">
                {description}
              </small>
              <small className="badge bg-secondary ms-2" style={{ fontSize: '0.6em' }}>
                {variable.type}
              </small>
            </div>
          </div>

          {/* Functions toggle */}
          {availableFunctions.length > 0 && (
            <button
              className="btn btn-sm btn-outline-secondary ms-2"
              onClick={() => setShowFunctions(!showFunctions)}
              title={`${availableFunctions.length} functions available`}
            >
              🔧 {availableFunctions.length} {showFunctions ? '▲' : '▼'}
            </button>
          )}
        </div>

        {/* Functions List */}
        {showFunctions && availableFunctions.length > 0 && (
          <div className="mt-2 border-top pt-2">
            <small className="text-muted d-block mb-1">Available Functions:</small>
            {availableFunctions.map((func) => (
              <div
                key={func.function}
                className="cursor-pointer hover-bg-white rounded px-2 py-1 mb-1"
                onClick={() => handleFunctionClick(func.function, variable.name)}
                title={`Insert [[ ${func.function}({{${variable.name}}}) ]] - ${func.description}`}
                style={{ cursor: 'pointer' }}
              >
                <div className="d-flex align-items-center">
                  <small className="text-success me-2">📋</small>
                  <small className="fw-medium">{func.label}</small>
                  <small className="text-muted ms-auto">{func.function}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}